import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

import { API_ENDPOINTS } from '../../../common/apiEndpoints'
import {
  ParkingSpotEquipments,
  ParkingSpotEquipment,
} from '../../../common/types'

import { get } from 'api/apiClient'
import { useCurrentContract } from 'hooks/useCurrentContract/useCurrentContract'

const queueItemSchema = z.object({
  id: z.number(),
  equipmentWishes: z.array(z.enum(ParkingSpotEquipments)),
  kind: z.enum(['own', 'other']),
  queuePosition: z.number(),
  additionalInformation: z.string().nullable().optional(),
  costPoolId: z.number(),
})

const getQueueResponseSchema = z.object({
  data: z.array(queueItemSchema),
  statusCode: z.number().optional(),
})

export type EquipmentWish = ParkingSpotEquipment

export type QueueEntry = {
  id: number
  queuePosition: number
  kind: 'own' | 'other'
  equipmentWishes: EquipmentWish[]
  additionalInformation?: string | null
  costPoolId: number
}

export const parkingQueueKey = (contractUuid: string) =>
  ['contracts', contractUuid, 'parking-queue', 'own'] as const

const buildUrl = (contractUuid: string) =>
  API_ENDPOINTS.RESIDENT_CONTRACT_PARKING_QUEUE.replace(
    ':contractUuid',
    contractUuid
  )

export const useAPIGetParkingQueue = () => {
  const { currentContractId } = useCurrentContract()

  return useQuery<QueueEntry[]>({
    queryKey: currentContractId
      ? parkingQueueKey(currentContractId)
      : ['parking-queue', 'missing-contract'],
    enabled: Boolean(currentContractId),
    queryFn: async () => {
      const json = await get(
        buildUrl(currentContractId),
        getQueueResponseSchema
      )
      return (json.data || []).map<QueueEntry>((it) => ({
        id: it.id,
        queuePosition: it.queuePosition,
        kind: it.kind,
        equipmentWishes: it.equipmentWishes as EquipmentWish[],
        additionalInformation: it.additionalInformation ?? null,
        costPoolId: it.costPoolId,
      }))
    },
  })
}
